/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: properties entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the properties table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the properties API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, parent_property_id, property_type_id, property_status_id, building_class_id, primary_use_id, secondary_use_id, name, external_ref, address_line_1, suite, city, county, state, postal_code, country, full_address, year_built, year_renovated, building_sf, space_sf, land_sf, land_ac, floors, parking_spaces, parking_ratio, zoning, legal_description, notable_features, research_notes, source_summary, verified_at, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, created_at, updated_at, latitude, longitude, submarket_id, trig_submarket_name, type_specific_fields, review_status_id, reviewed_at, reviewed_by_user_profile_id, owner_user, assessors_notes, is_investment, source_document_link, tenancy, created_by_ingestion_job_id, description, generation, snapshot_of_property_id, snapshot_label, snapshot_as_of, common_name, parking_type_id, construction_purpose_id, business_park, building_far, property_rights_id, is_test_data, unit_kind_id, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id]
 * Searchable fields: [name, address_line_1, city, full_address, trig_submarket_name]
 * Filterable fields: [id, parent_property_id, property_type_id, property_status_id, building_class_id, primary_use_id, secondary_use_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, submarket_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, snapshot_of_property_id, parking_type_id, construction_purpose_id, property_rights_id, is_test_data, unit_kind_id, archived_by_user_profile_id, archive_batch_id]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig, type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  PropertiesRow,
  PropertiesInsert,
  PropertiesUpdate
} from '../../types/generated_schemas/generated-properties-schema.js';

export interface PropertiesQueryOptions extends UnifiedQueryOptions {}

export interface PropertiesResponse {
  data: PropertiesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for properties table operations
 *
 * @description Provides type-safe access to properties API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class PropertiesClient {
  private readonly basePath = '/properties';

  /**
   * Get all properties records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of properties records
   */
  async getAll(options?: PropertiesQueryOptions): Promise<PropertiesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<PropertiesResponse>(url);
    return response.data;
  }

  /**
   * Get a single properties record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the properties record
   */
  async getById(id: string): Promise<PropertiesRow> {
    const response = await httpClient.get<{
      data: PropertiesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new properties record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created properties record
   */
  async create(data: PropertiesInsert): Promise<PropertiesRow> {
    const response = await httpClient.post<{
      data: PropertiesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing properties record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated properties record
   */
  async update(id: string, data: PropertiesUpdate): Promise<PropertiesRow> {
    const response = await httpClient.put<{
      data: PropertiesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a properties record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple properties records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created properties records
   */
  async createMany(data: PropertiesInsert[]): Promise<PropertiesRow[]> {
    const response = await httpClient.post<{
      data: PropertiesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search properties with text query
   *
   * @description Performs intelligent search across properties records with support for:
   * - Multi-term search: When enabled (globalSearchConfig.multiTermSearch = true),
   *   searches for ALL terms in the query. For example, "john smith" will find records
   *   containing both "john" AND "smith" anywhere in the searchable fields.
   * - Case sensitivity: Controlled by globalSearchConfig.caseSensitive (false)
   * - Minimum search length: 2 characters
   *
   * 🔍 Searchable fields: [name, address_line_1, city, full_address, trig_submarket_name]
   * 📊 Default limit: 100
   * 🔤 Multi-term search: Enabled
   *
   * @param query - Search query string. If multi-term search is enabled, space-separated
   *                terms will be treated as individual search terms that must ALL match.
   * @param columns - Array of field names to search in. Defaults to primary searchable fields.
   * @param options - Additional search options including pagination
   * @returns Promise resolving to array of matching properties records
   *
   * @example
   * // Single term search
   * await search("project");
   *
   * // Multi-term search (when enabled) - finds records with BOTH terms
   * await search("project alpha");
   *
   * // Search specific fields with pagination
   * await search("project", ["name", "description"], { limit: 20, offset: 0 });
   */
  async search(
    query: string,
    columns: string[] = ['name', 'address_line_1', 'city'],
    options?: { limit?: number; offset?: number }
  ): Promise<PropertiesRow[]> {
    // Validate minimum search length using global config
    if (query.length < globalSearchConfig.minSearchLength) {
      throw new Error(`Search query must be at least ${globalSearchConfig.minSearchLength} characters long.`);
    }

    const params = new URLSearchParams({
      q: query,
      columns: columns.join(','),
      ...(options?.limit && { limit: options.limit.toString() }),
      ...(options?.offset && { offset: options.offset.toString() })
    });

    const response = await httpClient.get<{
      data: PropertiesRow[];
      success: boolean;
    }>(`${this.basePath}/search?${params.toString()}`);
    
    return response.data;
  }

  /**
   * Get count of properties records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a properties record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'properties',
      publicFields: ['id', 'parent_property_id', 'property_type_id', 'property_status_id', 'building_class_id', 'primary_use_id', 'secondary_use_id', 'name', 'external_ref', 'address_line_1', 'suite', 'city', 'county', 'state', 'postal_code', 'country', 'full_address', 'year_built', 'year_renovated', 'building_sf', 'space_sf', 'land_sf', 'land_ac', 'floors', 'parking_spaces', 'parking_ratio', 'zoning', 'legal_description', 'notable_features', 'research_notes', 'source_summary', 'verified_at', 'verified_by_user_profile_id', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'created_at', 'updated_at', 'latitude', 'longitude', 'submarket_id', 'trig_submarket_name', 'type_specific_fields', 'review_status_id', 'reviewed_at', 'reviewed_by_user_profile_id', 'owner_user', 'assessors_notes', 'is_investment', 'source_document_link', 'tenancy', 'created_by_ingestion_job_id', 'description', 'generation', 'snapshot_of_property_id', 'snapshot_label', 'snapshot_as_of', 'common_name', 'parking_type_id', 'construction_purpose_id', 'business_park', 'building_far', 'property_rights_id', 'is_test_data', 'unit_kind_id', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id'],
      searchableFields: ['name', 'address_line_1', 'city', 'full_address', 'trig_submarket_name'],
      filterableFields: ['id', 'parent_property_id', 'property_type_id', 'property_status_id', 'building_class_id', 'primary_use_id', 'secondary_use_id', 'verified_by_user_profile_id', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'submarket_id', 'review_status_id', 'reviewed_by_user_profile_id', 'created_by_ingestion_job_id', 'snapshot_of_property_id', 'parking_type_id', 'construction_purpose_id', 'property_rights_id', 'is_test_data', 'unit_kind_id', 'archived_by_user_profile_id', 'archive_batch_id'],
      sortableFields: ['name', 'year_built', 'building_sf', 'verified_at', 'created_at', 'updated_at', 'snapshot_as_of'],
      readonlyFields: ['id', 'county', 'source_summary', 'verified_at', 'verified_by_user_profile_id', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'created_at', 'updated_at', 'submarket_id', 'trig_submarket_name', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id'],
      requiredFields: ['id', 'type_specific_fields'],
    };
  }
}

// Export singleton instance for convenience
export const propertiesClient = new PropertiesClient();
